import React, { useEffect, useState } from 'react';
import Background from '../components/Background';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { getReadingHistory } from '../services/api';
import logger from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ChartView from '../components/ChartView';

// Pastel planet colors matching Landing page
const planetColors: Record<string, string> = {
  'sun': '#FFE5A0',
  'moon': '#E8E4F0',
  'mercury': '#B8E6F5',
  'venus': '#FFD4E5',
  'mars': '#FF9A8B',
  'jupiter': '#C9B5E8',
  'saturn': '#9B7BC8',
  'uranus': '#7FE8E8',
  'neptune': '#9BB5FF',
  'rahu': '#C9B5E8',
  'ketu': '#9B7BC8',
};

type MetricItem = {
  key: string;
  title: string;
  preview: string;
  related_planets: string[];
  related_houses: number[];
};

type InfluenceItem = {
  planet: string;
  summary: string;
  house?: number;
  sign?: string;
  retrograde?: boolean;
  impacts: Array<{ metric: string; note: string }>;
};

const Profile: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const {
    profile,
    metrics: rawMetrics,
    influences: rawInfluences,
    personalityCards,
    planetBlurbs,
    chart,
    isLoading,
    error,
    refreshProfile
  } = useProfile();

  const metrics = rawMetrics as MetricItem[];
  const influences = rawInfluences as InfluenceItem[];

  const [style, setStyle] = useState<'western' | 'vedic'>('western');
  const [history, setHistory] = useState<any[]>([]);
  const [activeMetric, setActiveMetric] = useState<MetricItem | null>(null);
  const [chartMode, setChartMode] = useState<'western' | 'vedic'>('western');
  const [planetIndex, setPlanetIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [personalityIndex, setPersonalityIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'personality' | 'planets' | 'chart'>('personality');

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const h = await getReadingHistory({ page: 1, limit: 3 });
        setHistory(h.results || []);
      } catch (e) {
        logger.warn('Profile', 'Failed to load history', { error: e });
      }
    };
    loadHistory();

    // If we have metrics, set style
    if (metrics.length > 0) {
      // We don't have style in context directly, but we can infer or just keep default
      // Actually, getProfileMetrics returns style. We might need to expose it in context if important.
      // For now, default to western is fine or we can add it to context later.
    }
  }, [metrics]);

  if (isLoading && !profile) {
    return (
      <div className="min-h-screen relative" style={{ backgroundColor: '#050508' }}>
        <Background />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-2 border-cosmic-gold border-t-transparent rounded-full animate-spin" />
            <p className="text-text-secondary font-elegant animate-pulse">Aligning your stars...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="min-h-screen relative" style={{ backgroundColor: '#050508' }}>
        <Background />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-400 font-elegant mb-4">{error}</p>
            <button
              onClick={() => refreshProfile()}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white font-elegant transition-colors"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fullName = profile?.user?.name || user?.email || 'You';
  const birthLine = profile?.has_birth_chart
    ? `${profile.user?.birth_date || ''} • ${profile.user?.birth_city || ''}`
    : 'Complete onboarding to see your chart';

  const getPlanetColor = (planetName: string): string => {
    const lower = planetName.toLowerCase();
    for (const [key, color] of Object.entries(planetColors)) {
      if (lower.includes(key)) return color;
    }
    return '#E5E7EB';
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#050508]">
      <Background />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12">

        {/* Header */}
        <div className="flex flex-col items-center mb-16 animate-fade-in">
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-heading text-4xl mb-6 relative group">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-cosmic-gold/10 to-cosmic-purple/10 animate-pulse-glow" />
            <div className="absolute inset-0 rounded-full border border-white/10 group-hover:border-cosmic-gold/30 transition-colors duration-500" />
            {fullName?.[0]?.toUpperCase() || 'U'}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading text-white tracking-luxury mb-3 text-center">
            {fullName}
          </h1>
          <div className="flex items-center gap-3 text-text-secondary font-elegant text-sm tracking-widest uppercase opacity-60">
            <span>{birthLine}</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center mb-16">
          <div className="flex items-center gap-8 sm:gap-12 border-b border-white/5 px-8">
            {(['personality', 'planets', 'chart'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-4 font-elegant text-lg tracking-widest uppercase transition-all duration-500 relative
                  ${activeTab === tab ? 'text-cosmic-gold' : 'text-text-secondary hover:text-white/60'}
                `}
              >
                {tab}
                <span className={`
                  absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cosmic-gold to-transparent transition-opacity duration-500
                  ${activeTab === tab ? 'opacity-100' : 'opacity-0'}
                `} />
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="min-h-[500px] mb-24">
          {activeTab === 'personality' && (
            <div className="animate-fade-in max-w-md mx-auto perspective-1000">
              {personalityCards.length > 0 ? (
                <div className="relative min-h-[600px] flex flex-col">
                  {/* Stack Effect Background Cards */}
                  <div
                    className="absolute inset-0 bg-white/5 rounded-3xl transform translate-y-4 scale-95 z-0 border border-white/5"
                    style={{ transform: 'translateY(16px) scale(0.95)' }}
                  />
                  <div
                    className="absolute inset-0 bg-white/5 rounded-3xl transform translate-y-8 scale-90 z-0 border border-white/5"
                    style={{ transform: 'translateY(32px) scale(0.9)' }}
                  />

                  {/* Main Card */}
                  <div
                    className="glass-grain rounded-3xl p-8 sm:p-10 border border-white/10 relative overflow-hidden group transition-all duration-500 flex flex-col h-full z-10 bg-[#0A0A0F]/80 backdrop-blur-xl shadow-2xl"
                    onTouchStart={(e) => setTouchStartX(e.changedTouches[0].clientX)}
                    onTouchEnd={(e) => {
                      const endX = e.changedTouches[0].clientX;
                      if (touchStartX !== null) {
                        const dx = endX - touchStartX;
                        if (dx > 50) setPersonalityIndex((i) => Math.max(0, i - 1));
                        if (dx < -50) setPersonalityIndex((i) => Math.min(personalityCards.length - 1, i + 1));
                      }
                      setTouchStartX(null);
                    }}
                  >
                    {/* Card Header */}
                    <div className="flex justify-between items-start mb-6 shrink-0">
                      <div className="text-cosmic-gold font-elegant text-sm tracking-widest uppercase border border-cosmic-gold/30 px-3 py-1 rounded-full bg-cosmic-gold/5">
                        {personalityCards[personalityIndex].tag}
                      </div>
                      <div className="text-6xl font-heading text-white/5 absolute top-4 right-6 pointer-events-none">
                        {(personalityIndex + 1).toString().padStart(2, '0')}
                      </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar mb-8">
                      <h3 className="text-3xl sm:text-4xl font-heading text-white mb-6 leading-tight">
                        {personalityCards[personalityIndex].title}
                      </h3>
                      <p className="text-text-secondary font-elegant text-lg sm:text-xl leading-relaxed whitespace-pre-line">
                        {personalityCards[personalityIndex].body}
                      </p>
                    </div>

                    {/* Footer / Navigation */}
                    <div className="shrink-0 pt-6 border-t border-white/5">
                      <div className="flex items-center justify-between gap-4">
                        <button
                          onClick={() => setPersonalityIndex((i) => Math.max(0, i - 1))}
                          disabled={personalityIndex === 0}
                          className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-20 disabled:hover:bg-transparent transition-all"
                          aria-label="Previous card"
                        >
                          ←
                        </button>

                        <div className="flex gap-1">
                          {personalityCards.map((_: any, idx: number) => (
                            <div
                              key={idx}
                              className={`h-1.5 rounded-full transition-all duration-300 ${idx === personalityIndex ? 'w-6 bg-cosmic-gold' : 'w-1.5 bg-white/20'}`}
                            />
                          ))}
                        </div>

                        <button
                          onClick={() => setPersonalityIndex((i) => Math.min(personalityCards.length - 1, i + 1))}
                          disabled={personalityIndex === personalityCards.length - 1}
                          className="h-12 px-6 rounded-full bg-cosmic-gold text-[#050508] font-heading font-bold text-lg hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(255,184,79,0.3)] flex items-center gap-2 whitespace-nowrap"
                        >
                          Next →
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          const q = `Tell me more about my personality, specifically "${personalityCards[personalityIndex].title}".`;
                          navigate(`/reading?type=custom&autofill=true&question=${encodeURIComponent(q)}&mode=${style}`);
                        }}
                        className="w-full mt-4 py-3 text-cosmic-gold/80 hover:text-cosmic-gold transition-colors font-elegant text-base italic border border-dashed border-white/10 hover:border-cosmic-gold/30 rounded-xl"
                      >
                        Dive Deeper into this Trait
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-20 text-text-secondary font-elegant text-xl">
                  Your personality analysis is being generated...
                </div>
              )}
            </div>
          )}

          {activeTab === 'planets' && (
            <div className="animate-fade-in max-w-3xl mx-auto">
              {influences.length > 0 ? (
                <div className="relative">
                  {/* Planet Card */}
                  {(() => {
                    const inf = influences[planetIndex];
                    const blurbText = (() => {
                      const planetName = (inf?.planet || '').toLowerCase();
                      const found = planetBlurbs.find(b => b.planet.toLowerCase().includes(planetName));
                      return found?.text || inf?.summary || '';
                    })();
                    const color = getPlanetColor(inf.planet);

                    return (
                      <div className="relative min-h-[600px] flex flex-col">
                        {/* Stack Effect Background Cards */}
                        <div
                          className="absolute inset-0 bg-white/5 rounded-3xl transform translate-y-4 scale-95 z-0 border border-white/5"
                          style={{ transform: 'translateY(16px) scale(0.95)' }}
                        />
                        <div
                          className="absolute inset-0 bg-white/5 rounded-3xl transform translate-y-8 scale-90 z-0 border border-white/5"
                          style={{ transform: 'translateY(32px) scale(0.9)' }}
                        />

                        {/* Main Card */}
                        <div
                          className="glass-grain rounded-3xl p-8 sm:p-10 border border-white/10 relative overflow-hidden transition-all duration-500 flex flex-col h-full z-10 bg-[#0A0A0F]/80 backdrop-blur-xl shadow-2xl"
                          style={{
                            boxShadow: `0 0 100px ${color}10`
                          }}
                          onTouchStart={(e) => setTouchStartX(e.changedTouches[0].clientX)}
                          onTouchEnd={(e) => {
                            const endX = e.changedTouches[0].clientX;
                            if (touchStartX !== null) {
                              const dx = endX - touchStartX;
                              if (dx > 50) setPlanetIndex((i) => Math.max(0, i - 1));
                              if (dx < -50) setPlanetIndex((i) => Math.min(influences.length - 1, i + 1));
                            }
                            setTouchStartX(null);
                          }}
                        >
                          {/* Background Glow */}
                          <div
                            className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-[100px] opacity-20 pointer-events-none"
                            style={{ backgroundColor: color }}
                          />

                          <div className="relative z-10 flex flex-col h-full">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 shrink-0">
                              <div className="flex items-center gap-4">
                                <div
                                  className="w-12 h-12 rounded-full flex items-center justify-center font-heading text-xl shadow-lg"
                                  style={{
                                    backgroundColor: `${color}20`,
                                    color: color,
                                    border: `1px solid ${color}40`
                                  }}
                                >
                                  {inf.planet[0]}
                                </div>
                                <div>
                                  <h3 className="text-3xl font-heading text-white">{inf.planet}</h3>
                                  <div className="text-text-secondary font-elegant text-sm flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{inf.sign}</span>
                                    {inf.house && <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10">House {inf.house}</span>}
                                    {inf.retrograde && <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">Retrograde</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="text-text-tertiary font-elegant text-sm uppercase tracking-widest border border-white/10 px-3 py-1 rounded-full">
                                {style === 'vedic' ? 'Vedic' : 'Western'}
                              </div>
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar mb-8">
                              <p className="text-text-secondary font-elegant text-lg sm:text-xl leading-relaxed whitespace-pre-line mb-8">
                                {blurbText}
                              </p>

                              {inf.impacts.length > 0 && (
                                <div className="flex flex-wrap gap-3">
                                  {inf.impacts.map((imp, idx) => (
                                    <span
                                      key={idx}
                                      className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 text-text-secondary font-elegant text-sm hover:bg-white/10 transition-colors"
                                    >
                                      <span className="text-cosmic-gold font-semibold">{imp.metric}:</span> {imp.note}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Footer / Navigation */}
                            <div className="shrink-0 pt-6 border-t border-white/5">
                              <div className="flex items-center justify-between gap-4">
                                <button
                                  onClick={() => setPlanetIndex((i) => Math.max(0, i - 1))}
                                  disabled={planetIndex === 0}
                                  className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-20 disabled:hover:bg-transparent transition-all"
                                  aria-label="Previous planet"
                                >
                                  ←
                                </button>

                                <div className="flex gap-1">
                                  {influences.map((_: any, idx: number) => (
                                    <div
                                      key={idx}
                                      className={`h-1.5 rounded-full transition-all duration-300 ${idx === planetIndex ? 'w-6' : 'w-1.5 bg-white/20'}`}
                                      style={{ backgroundColor: idx === planetIndex ? color : undefined }}
                                    />
                                  ))}
                                </div>

                                <button
                                  onClick={() => setPlanetIndex((i) => Math.min(influences.length - 1, i + 1))}
                                  disabled={planetIndex === influences.length - 1}
                                  className="h-12 px-6 rounded-full text-[#050508] font-heading font-bold text-lg hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center gap-2 whitespace-nowrap"
                                  style={{ backgroundColor: color }}
                                >
                                  Next →
                                </button>
                              </div>

                              <div className="text-center mt-4 text-white/20 font-heading text-sm">
                                {(planetIndex + 1).toString().padStart(2, '0')} <span className="opacity-50">/ {influences.length}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-20 text-text-secondary font-elegant text-xl">
                  Aligning planetary data...
                </div>
              )}
            </div>
          )}

          {activeTab === 'chart' && (
            <div className="animate-fade-in max-w-4xl mx-auto">
              <div className="glass-grain rounded-2xl p-8 border border-white/5">
                <div className="flex justify-center gap-6 mb-8">
                  <button
                    onClick={() => setChartMode('western')}
                    className={`px-6 py-2 rounded-full font-elegant text-sm transition-all ${chartMode === 'western'
                      ? 'bg-cosmic-gold/20 text-cosmic-gold border border-cosmic-gold/40'
                      : 'text-text-secondary hover:text-white border border-transparent'
                      }`}
                  >
                    Western
                  </button>
                  <button
                    onClick={() => setChartMode('vedic')}
                    className={`px-6 py-2 rounded-full font-elegant text-sm transition-all ${chartMode === 'vedic'
                      ? 'bg-cosmic-gold/20 text-cosmic-gold border border-cosmic-gold/40'
                      : 'text-text-secondary hover:text-white border border-transparent'
                      }`}
                  >
                    Vedic
                  </button>
                </div>
                <div className="flex justify-center">
                  <ChartView
                    mode={chartMode}
                    western={chart?.chart_data_western}
                    vedic={chart?.chart_data_vedic}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Life Metrics */}
        <div className="mb-24 animate-fade-in">
          <h2 className="text-2xl font-heading text-white text-center mb-12 tracking-wide">Your Cosmic Blueprint</h2>
          {/* Life Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
            {metrics.map((metric, idx) => {
              // Assign a pastel color based on index if no specific mapping
              const colors = Object.values(planetColors);
              const color = colors[idx % colors.length];

              return (
                <div
                  key={metric.key}
                  className="glass-grain rounded-2xl p-8 backdrop-blur-xl border border-white/5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 group"
                  style={{
                    background: `linear-gradient(145deg, rgba(20, 20, 32, 0.6) 0%, ${color}05 100%)`,
                    borderColor: `${color}20`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${color}50`;
                    e.currentTarget.style.boxShadow = `0 10px 40px -10px ${color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${color}20`;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => setActiveMetric(metric)}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:rotate-12"
                      style={{
                        backgroundColor: `${color}15`,
                        border: `1px solid ${color}30`,
                        color: color
                      }}
                    >
                      {/* Dynamic Icon based on metric key or index */}
                      {idx % 4 === 0 ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      ) : idx % 4 === 1 ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                      ) : idx % 4 === 2 ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                      )}
                    </div>
                    <div className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[10px] uppercase tracking-widest text-text-tertiary group-hover:bg-white/10 transition-colors">
                      Metric
                    </div>
                  </div>

                  <h3 className="text-2xl font-heading text-white mb-3 group-hover:text-gradient-gold transition-all">
                    {metric.title}
                  </h3>

                  <p className="text-text-secondary font-elegant text-lg leading-relaxed line-clamp-3 mb-6 group-hover:text-white/90 transition-colors">
                    {metric.preview}
                  </p>

                  <div className="flex items-center gap-2 text-sm font-medium transition-colors" style={{ color: color }}>
                    <span>View Analysis</span>
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent History */}
        <div className="max-w-3xl mx-auto animate-fade-in">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-heading text-white tracking-wide">Your Journey So Far</h2>
            <button
              onClick={() => navigate('/profile/history')}
              className="text-cosmic-gold hover:text-white font-elegant text-sm transition-colors"
            >
              View All
            </button>
          </div>

          <div className="space-y-4">
            {history.map((h) => (
              <div
                key={h.id}
                onClick={() => navigate(`/reading/view/${h.id}`)}
                className="glass-grain rounded-xl p-6 border border-white/5 hover:border-white/20 transition-all cursor-pointer group flex items-center justify-between gap-4"
              >
                <div>
                  <div className="text-xs font-elegant text-cosmic-gold uppercase tracking-widest mb-2">
                    {h.session_type} • {new Date(h.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-white font-heading text-lg group-hover:text-cosmic-gold transition-colors">
                    "{h.initial_question}"
                  </div>
                </div>
                <div className="text-white/20 group-hover:text-white transition-colors text-2xl">
                  →
                </div>
              </div>
            ))}
            {history.length === 0 && (
              <div className="text-center text-text-secondary font-elegant py-8">No readings yet.</div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-center gap-8 mt-24 pb-12 border-t border-white/5 pt-12">
          <button
            onClick={() => navigate('/profile/settings')}
            className="text-text-secondary hover:text-white font-elegant text-sm tracking-widest uppercase transition-colors"
          >
            Preferences
          </button>
          <button
            onClick={async () => { await signOut(); navigate('/'); }}
            className="text-text-secondary hover:text-red-400 font-elegant text-sm tracking-widest uppercase transition-colors"
          >
            Sign Out
          </button>
        </div>

      </div>

      {/* Modals */}
      {activeMetric && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setActiveMetric(null)}>
          <div className="max-w-lg w-full glass-grain rounded-2xl border border-white/10 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-heading text-white">{activeMetric.title}</h3>
              <button onClick={() => setActiveMetric(null)} className="text-white/50 hover:text-white">✕</button>
            </div>
            <p className="text-text-secondary font-elegant text-lg leading-relaxed mb-8">
              {activeMetric.preview}
            </p>
            <div className="space-y-4">
              <div>
                <div className="text-xs font-elegant text-cosmic-gold uppercase tracking-widest mb-2">Related Planets</div>
                <div className="flex flex-wrap gap-2">
                  {activeMetric.related_planets.map((p: string) => (
                    <span key={p} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white">{p}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
